// analytics.* — client-initiated growth/analytics instrumentation events.
//
// These are the only public client→server analytics events. All other
// analytics (session.start / session.end / room.member_count_observed) is
// derived server-side and never carried on the WS wire; those live entirely
// inside the server's internal batch path (analytics.md §4.5). The events here
// exist because some product moments happen *only in the browser* and the
// server has no other way to observe them.
//
// `analytics.timelapse-share` measures the 絵ログ (timelapse) X-share funnel
// (growth-timelapse-share-2026-07-02.md §5.4): how many timelapse videos are
// generated vs. how many shares actually complete. The design decision to use
// a WS event (rather than a public HTTP endpoint) is recorded in §5.3.4 /
// §5.4: the share always fires while the user is in a room, so it rides the
// already-authenticated WS connection with no new auth surface.
//
// `analytics.layout-switch` measures Classic ⇄ Modern layout switches during a
// session (pen-usage-analytics-2026-08-18.md §9). The server has no other way
// to observe this — the current layout lives entirely in the client's
// localStorage. Unlike the entry-time value (carried on `room.join.layout`,
// see room.ts), a switch happens mid-session and needs its own event. The
// server does not act on the value; it only forwards it into the per-session
// analytics memo so `session.end` can record the final layout + how many
// times it changed (pen-usage-analytics-2026-08-18.md §9.3 — direction
// matters for the "Modern = CSP gateway" hypothesis, not just the entry
// value).
//
// Authorization: in-room member only. The server derives the room from
// `connection.roomId` (same as `stroke.*` / `feedback.submit`), so no roomId is
// carried on the payload. There is no server response — this is fire-and-forget
// instrumentation; a failed send must never block the layout toggle UX.
import { z } from 'zod';
import { layoutKindSchema } from '../shared/layout.js';
/**
 * The two moments the timelapse-share funnel measures
 * (growth-timelapse-share-2026-07-02.md §5.4):
 *
 * - `generate` — the timelapse video was successfully generated (= 生成数).
 * - `share`    — the share action completed: Web Share resolved, or the PC
 *                download + X intent was opened (= 共有完了数). A user-cancelled
 *                Web Share (AbortError) is deliberately NOT reported here — the
 *                client suppresses it (§5.3.4 黙殺方針).
 */
export const TIMELAPSE_SHARE_PHASES = ['generate', 'share'];
/**
 * `analytics.timelapse-share` — c→s.
 *
 * Fire-and-forget instrumentation ping sent from the timelapse share modal at
 * two moments (see `TIMELAPSE_SHARE_PHASES`). Recorded server-side into the
 * existing `analytics_events` table as `event_type = 'share.timelapse'` with a
 * `phase` discriminator (rides the same 30s analytics batch path as
 * session.start etc. — see server AnalyticsRepository).
 *
 * Permission: none beyond being a joined member of a room. Room id is taken
 * from the connection, never from the payload (avoids trusting a client-sent
 * room id + keeps parity with the live-tail request).
 */
export const analyticsTimelapseShareSchema = z.object({
    t: z.literal('analytics.timelapse-share'),
    phase: z.enum(TIMELAPSE_SHARE_PHASES),
});
/**
 * The three moments the 「友達を招待」 dialog funnel measures
 * (room-invite-2026-10-01.md):
 *
 * - `open`  — the invite dialog was opened.
 * - `copy`  — the URL was copied to the clipboard successfully.
 * - `share` — `navigator.share` resolved (OS share sheet handed the URL on).
 *             A user-cancelled share (AbortError) is NOT reported — same
 *             silent policy as `analytics.timelapse-share`.
 */
export const INVITE_ACTIONS = ['open', 'copy', 'share'];
/**
 * `analytics.invite` — c→s.
 *
 * Fire-and-forget instrumentation ping from the invite dialog. Recorded
 * server-side into `analytics_events` as `event_type = 'share.invite'` with the
 * `action` stored in the existing `phase` column (no schema change). Room id
 * comes from the connection, never the payload. Permission: joined member.
 */
export const analyticsInviteSchema = z.object({
    t: z.literal('analytics.invite'),
    action: z.enum(INVITE_ACTIONS),
});
/**
 * 「困りごとのコツ」カードの種別 (aa-default-and-trouble-tips-2026-10-27.md).
 * 今後コツが増えたらここに足す. server は `tip.<tipId>` を event_type にする.
 */
export const TROUBLE_TIP_IDS = ['aa-smooth'];
/**
 * コツカードで計測するアクション.
 *
 * - `shown`   — カードが表示された.
 * - `accept`  — 「なめらかにする」(提案を受け入れた).
 * - `dismiss` — 「このままでいい」.
 * - `detail`  — 「くわしく」.
 */
export const TIP_ACTIONS = ['shown', 'accept', 'dismiss', 'detail'];
/**
 * `analytics.tip` — c→s.
 *
 * Fire-and-forget instrumentation ping from the trouble-tip card. Recorded
 * server-side into `analytics_events` as `event_type = 'tip.<tipId>'` with the
 * `action` stored in the existing `phase` column (no schema change). Room id
 * comes from the connection, never the payload. Permission: joined member.
 */
export const analyticsTipSchema = z.object({
    t: z.literal('analytics.tip'),
    tipId: z.enum(TROUBLE_TIP_IDS),
    action: z.enum(TIP_ACTIONS),
});
/**
 * `analytics.layout-switch` — c→s (pen-usage-analytics-2026-08-18.md §9).
 *
 * Fire-and-forget instrumentation ping sent whenever the user explicitly
 * toggles Classic ⇄ Modern layout (the same explicit-toggle moment that
 * writes `LAYOUT_KEY` to `localStorage` client-side — not every render / every
 * mobile-forced-Modern resolution). `layout` is the value **after** the
 * switch. Recorded server-side as an update to the per-session analytics memo
 * (`AnalyticsSessionTracker`): the memo's "current layout" is overwritten and
 * its switch counter incremented, both flushed into `session.end` at close
 * time (`layout` / `layout_switch_count` columns) — no new `analytics_events`
 * row is written per switch (pen-usage-analytics-2026-08-18.md §9.3: entry +
 * exit + count is enough for the questions at hand; a fine-grained per-switch
 * timeline can be added later additively as its own event if ever needed).
 *
 * Permission: none beyond being a joined member of a room (same as
 * `analytics.timelapse-share`).
 */
export const analyticsLayoutSwitchSchema = z.object({
    t: z.literal('analytics.layout-switch'),
    layout: layoutKindSchema,
});
/**
 * The two touch-gesture moments the pinch measurement counts
 * (modern-phone-shell-2026-07-02.md §7):
 *
 * - `started` — a two-finger gesture (pan + pinch) was entered. Counts both the
 *               grace-window path and the penOnlyMode touch path.
 * - `late`    — a second finger landed **after** the touch grace window
 *               (`TOUCH_GRACE_WINDOW_MS`) had already started a stroke, so it
 *               was ignored and the first finger kept drawing. This is the
 *               "I pinched but it just drew a line" case.
 */
export const TOUCH_GESTURE_OUTCOMES = ['started', 'late'];
/**
 * A `late` second finger landing within this many ms of the first finger is
 * counted as "near" (= a longer grace window would likely have caught it).
 * Provisional boundary (§7, 要検証) — the server buckets with it so the
 * client only reports the raw delay.
 */
export const TOUCH_GESTURE_LATE_NEAR_MS = 150;
/** Upper bound on the reported delay; clients clamp to it. */
export const TOUCH_GESTURE_DELAY_MAX_MS = 60_000;
/**
 * `analytics.touch-gesture` — c→s (modern-phone-shell-2026-07-02.md §7).
 *
 * Fire-and-forget instrumentation ping sent each time a two-finger gesture
 * starts (`started`) or a late second finger is ignored (`late`). `delayMs` is
 * the time from the first finger's pointerdown to the second finger's, sent
 * with `late` only (ignored for `started`). Recorded server-side into the
 * per-session analytics memo and flushed into `session.end`
 * (`gesture_count` / `gesture_late_count` / `gesture_late_near_count`) — no
 * per-gesture `analytics_events` row, same as `analytics.layout-switch`.
 *
 * Permission: none beyond being a joined member of a room.
 */
export const analyticsTouchGestureSchema = z.object({
    t: z.literal('analytics.touch-gesture'),
    outcome: z.enum(TOUCH_GESTURE_OUTCOMES),
    delayMs: z.number().int().min(0).max(TOUCH_GESTURE_DELAY_MAX_MS).optional(),
});
//# sourceMappingURL=analytics.js.map